import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export function useIdentity() {
  const { user } = useAuth();
  const [identityText, setIdentityText] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load identity text from Supabase
  useEffect(() => {
    const loadIdentity = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_identity')
          .select('identity_text')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading identity:', error);
          // Fall back to localStorage if database fails
          const saved = localStorage.getItem('identityText');
          setIdentityText(saved || '');
        } else if (data) {
          setIdentityText(data.identity_text);
          // Sync to localStorage as backup
          localStorage.setItem('identityText', data.identity_text);
        } else {
          // No identity found, check localStorage for migration
          const saved = localStorage.getItem('identityText');
          if (saved && saved !== 'software engineer, family man, a man of god') {
            // Only migrate if it's not the old default value
            setIdentityText(saved);
            // Auto-save to database
            await saveIdentity(saved);
          } else {
            // New user or user with old default - start with empty
            setIdentityText('');
          }
        }
      } catch (error) {
        console.error('Error loading identity:', error);
        // Fall back to localStorage
        const saved = localStorage.getItem('identityText');
        setIdentityText(saved || '');
      } finally {
        setIsLoading(false);
      }
    };

    loadIdentity();
  }, [user]);

  // Save identity text to Supabase
  const saveIdentity = async (text: string) => {
    if (!user) {
      toast.error('You must be logged in to save your identity');
      return false;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('user_identity')
        .upsert({
          user_id: user.id,
          identity_text: text,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving identity:', error);
        toast.error('Failed to save identity');
        return false;
      }

      setIdentityText(text);
      // Also save to localStorage as backup
      localStorage.setItem('identityText', text);
      return true;
    } catch (error) {
      console.error('Error saving identity:', error);
      toast.error('Failed to save identity');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    identityText,
    isLoading,
    isSaving,
    saveIdentity
  };
} 